import { useState, type ReactNode } from 'react';

import { useTranslation } from '@mint-lib/i18n';
import { ChevronLeft, ChevronRight } from '@mint-lib/icons';
import { cn } from '@mint-lib/tw-utils';

import { Button } from '../../lib/button.jsx';
import { Tooltip, TooltipContent, TooltipTrigger } from '../../lib/tooltip.jsx';

export interface ExpandableSidebarProps {
  children?: ReactNode;
  /**
   * controlled open state
   */
  open?: boolean;
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  className?: string;
}

/**
 * Collapsible sidebar. When collapsed, hovering it temporarily expands the panel, pushing the page content aside.
 */
const ExpandableSidebar = ({
  children,
  open: openProp,
  defaultOpen = true,
  onOpenChange,
  className,
}: ExpandableSidebarProps) => {
  const { t } = useTranslation('@mint-lib/tw-components');

  const [uncontrolledOpen, setUncontrolledOpen] = useState(defaultOpen);
  const [isPeeking, setIsPeeking] = useState(false);

  const isOpen = openProp ?? uncontrolledOpen;
  const peeking = isPeeking && !isOpen;

  const toggleSidebar = () => {
    if (openProp === undefined) {
      setUncontrolledOpen(!isOpen);
    }
    setIsPeeking(false);
    onOpenChange?.(!isOpen);
  };

  return (
    <nav
      data-slot="expandable-sidebar"
      data-open={isOpen}
      data-expanded={isOpen || peeking}
      data-peeking={peeking}
      onMouseLeave={() => setIsPeeking(false)}
      className={cn(
        'group/sidebar h-full shrink-0 transition-[width] duration-300 data-[expanded=false]:w-4 data-[expanded=true]:w-50 data-[peeking=true]:delay-300',
        className,
      )}
    >
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            aria-label={isOpen ? t('Collapse') : t('Expand')}
            aria-expanded={isOpen}
            data-dd-action-name="Expandable sidebar: toggle sidebar"
            className="peer fixed z-4 mt-4 rounded-full border-ui-tertiary bg-ui-primary fill-icon-primary transition-[margin-left,background-color,border-color] duration-300 group-data-[expanded=false]/sidebar:ml-1 group-data-[expanded=true]/sidebar:ml-47 group-data-[peeking=true]/sidebar:delay-300 hover:border-ui-inverse01 hover:bg-ui-tertiary"
            icon
            onClick={toggleSidebar}
            size="small"
            type="button"
            variant="outlined"
          >
            {isOpen ? (
              <ChevronLeft aria-hidden />
            ) : (
              <ChevronRight aria-hidden />
            )}
          </Button>
        </TooltipTrigger>
        <TooltipContent side="right" sideOffset={8}>
          {isOpen ? t('Collapse') : t('Expand')}
        </TooltipContent>
      </Tooltip>
      <div
        onMouseEnter={() => setIsPeeking(!isOpen)}
        className="h-full w-full overflow-hidden border-r border-ui-tertiary bg-ui-primary transition-shadow duration-300 group-data-[peeking=true]/sidebar:shadow-[0px_0px_8px_0px_rgba(0,0,0,0.16)] group-data-[peeking=true]/sidebar:delay-300 peer-hover:border-ui-inverse01"
      >
        <div className="h-full w-50 overflow-x-hidden overflow-y-auto px-2 pt-4 pb-2 transition-opacity duration-300 group-data-[expanded=false]/sidebar:pointer-events-none group-data-[expanded=false]/sidebar:opacity-0 group-data-[peeking=true]/sidebar:delay-300">
          {children}
        </div>
      </div>
    </nav>
  );
};

export default ExpandableSidebar;
